import React, { createContext, useContext } from "react";
import { CaretRight, CaretLeft, Warning, ArrowClockwise } from "@phosphor-icons/react";
import { cn } from "@/lib/format";
import { ShareLinkButton } from "@/components/primitives";

// Governance-internal navigation (push a /governance/* route). Provided by
// GovernanceRouter so any nested card/link/breadcrumb can navigate.
export const GovNavContext = createContext(() => {});
export const useGovNav = () => useContext(GovNavContext);

export const GovBreadcrumbs = ({ crumbs = [] }) => {
  const navigate = useGovNav();
  return (
    <nav data-testid="gov-breadcrumbs" className="flex items-center gap-1.5 flex-wrap text-[10px] uppercase tracking-[0.18em] text-muted-foreground mono">
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        return (
          <span key={`${c.label}-${i}`} className="inline-flex items-center gap-1.5">
            {c.path && !last ? (
              <button
                type="button"
                data-testid={`crumb-${i}`}
                onClick={() => navigate(c.path)}
                className="hover:text-primary transition-colors"
              >
                {c.label}
              </button>
            ) : (
              <span className={cn(last && "text-foreground")}>{c.label}</span>
            )}
            {!last ? <CaretRight size={10} className="opacity-50" /> : null}
          </span>
        );
      })}
    </nav>
  );
};

export const GovPageShell = ({ crumbs, title, subtitle, actions, children }) => (
  <main
    data-testid="gov-page"
    className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6 sm:space-y-8"
  >
    <GovBreadcrumbs crumbs={crumbs} />
    <div className="flex items-end justify-between gap-6 flex-wrap">
      <div className="min-w-0">
        <h1 data-testid="gov-page-title" className="font-heading text-2xl md:text-3xl font-semibold tracking-tight">
          {title}
        </h1>
        {subtitle ? <p className="text-[12px] text-muted-foreground mono mt-2">{subtitle}</p> : null}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {actions}
        <ShareLinkButton />
      </div>
    </div>
    {children}
  </main>
);

// Clickable (or static) stat tile. Mirrors the explorer chain-tip glow aesthetic.
export const GovStat = ({ label, value, hint, icon: Icon, onClick, testid }) => {
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mono">{label}</div>
        {Icon ? <Icon size={15} className="text-primary/70" /> : null}
      </div>
      <div className="font-heading text-[18px] md:text-[20px] tabnum text-[#22d3ee] text-glow-cyan break-all leading-tight">{value}</div>
      {hint ? <div className="text-[10px] mono text-muted-foreground/80">{hint}</div> : null}
    </>
  );
  const base = "relative overflow-hidden rounded-2xl border border-primary/25 bg-card/50 backdrop-blur-md box-glow p-5 flex flex-col gap-2.5 text-left";
  if (onClick) {
    return (
      <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        className={cn(base, "transition-colors hover:border-[#22d3ee]/50")}
      >
        {body}
      </button>
    );
  }
  return (
    <div data-testid={testid} className={base}>
      {body}
    </div>
  );
};

export const GovSection = ({ title, action, children, testid, className }) => (
  <section data-testid={testid} className={cn("space-y-3", className)}>
    {(title || action) && (
      <div className="flex items-center justify-between gap-3">
        {title ? (
          <h2 className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mono">{title}</h2>
        ) : <span />}
        {action}
      </div>
    )}
    {children}
  </section>
);

export const GovCard = ({ children, className }) => (
  <div className={cn("relative overflow-hidden border border-primary/25 bg-card/50 backdrop-blur-md box-glow rounded-2xl p-5 sm:p-6", className)}>
    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-60 tracing-beam pointer-events-none" />
    {children}
  </div>
);

// Label / value row for parameter-style detail blocks.
export const GovRow = ({ label, children, testid }) => (
  <div className="flex items-start justify-between gap-6 py-3 border-b border-primary/10 last:border-b-0">
    <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mono shrink-0 pt-0.5">{label}</span>
    <span data-testid={testid} className="mono text-[13px] tabnum text-right break-all min-w-0">{children}</span>
  </div>
);

export const GovError = ({ error, onRetry, testid = "gov-error" }) => {
  const isClient = error?.status >= 400 && error?.status < 500;
  return (
  <div data-testid={testid} className="border border-red-500/40 bg-red-500/5 text-red-400 p-5 flex items-start gap-3 rounded-lg">
    <Warning size={18} className="mt-0.5 shrink-0" />
    <div className="min-w-0">
      <div className="text-sm font-semibold">{isClient ? "Invalid request" : "Couldn't load governance data"}</div>
      <div className="mono text-[12px] text-red-300/80 mt-1 break-words">
        {error?.message || "The governance data source is temporarily unavailable. Please retry."}
      </div>
      {onRetry && !isClient ? (
        <button
          type="button"
          data-testid={`${testid}-retry`}
          onClick={onRetry}
          className="mt-3 inline-flex items-center gap-1.5 border border-red-500/40 px-3 py-1 text-[10px] uppercase tracking-[0.15em] mono hover:bg-red-500/10 transition-colors rounded"
        >
          <ArrowClockwise size={12} /> Retry
        </button>
      ) : null}
    </div>
  </div>
  );
};

export const GovSkeleton = ({ lines = 3, testid = "gov-loading" }) => (
  <div data-testid={testid} className="relative overflow-hidden border border-primary/20 bg-card/50 backdrop-blur-md rounded-2xl p-5 space-y-3">
    {Array.from({ length: lines }).map((_, i) => (
      <div key={i} className="h-3 bg-muted/30 animate-pulse rounded-sm" style={{ width: `${90 - i * 12}%` }} />
    ))}
  </div>
);

export const GovEmpty = ({ children = "No data available.", testid = "gov-empty" }) => (
  <div data-testid={testid} className="border border-primary/15 bg-card/40 backdrop-blur-md rounded-2xl px-4 py-8 text-center text-muted-foreground mono text-[13px]">
    {children}
  </div>
);

// Tab strip for detail pages (proposal / drep / committee member).
export const GovTabs = ({ tabs, active, onChange, testid = "gov-tabs" }) => (
  <div data-testid={testid} className="flex items-center gap-1 flex-wrap border-b border-primary/15 overflow-x-auto">
    {tabs.map((t) => (
      <button
        key={t.key}
        type="button"
        data-testid={`gov-tab-${t.key}`}
        onClick={() => onChange(t.key)}
        className={cn(
          "px-3.5 py-2 text-[11px] uppercase tracking-[0.15em] mono border-b-2 -mb-px whitespace-nowrap transition-colors",
          active === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground",
        )}
      >
        {t.label}
      </button>
    ))}
  </div>
);

// Prev / next pager. `hasNext` driven by caller (rows.length === pageSize).
export const GovPager = ({ page, onPage, hasNext, loading, testid = "gov-pager" }) => {
  const btn = "inline-flex items-center gap-1.5 border px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-[0.15em] mono transition-colors";
  const disabled = "border-border/40 text-muted-foreground/40 cursor-not-allowed";
  const active = "border-primary/30 text-foreground hover:border-primary/60";
  return (
    <div data-testid={testid} className="flex items-center justify-between gap-3 pt-3">
      <button type="button" data-testid="gov-pager-prev" disabled={page <= 1 || loading} onClick={() => onPage(page - 1)}
        className={cn(btn, page <= 1 || loading ? disabled : active)}>
        <CaretLeft size={12} /> Prev
      </button>
      <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">Page {page}</span>
      <button type="button" data-testid="gov-pager-next" disabled={!hasNext || loading} onClick={() => onPage(page + 1)}
        className={cn(btn, !hasNext || loading ? disabled : active)}>
        Next <CaretRight size={12} />
      </button>
    </div>
  );
};

// Vote outcome chip (yes / no / abstain).
export const VoteChip = ({ vote }) => {
  const tone = vote === "yes"
    ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/5"
    : vote === "no"
      ? "text-red-400 border-red-500/40 bg-red-500/5"
      : "text-muted-foreground border-border";
  return (
    <span className={cn("inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest border rounded", tone)}>
      {vote || "—"}
    </span>
  );
};

// Stacked yes/no/abstain proportion bar. Values may be big integer-strings
// (voting power) or small counts — used only for bar widths (presentation).
export const GovTallyBar = ({ yes = 0, no = 0, abstain = 0, testid = "gov-tally-bar" }) => {
  const y = Number(yes) || 0, n = Number(no) || 0, a = Number(abstain) || 0;
  const total = y + n + a;
  const pct = (v) => (total > 0 ? `${((v / total) * 100).toFixed(1)}%` : "0%");
  return (
    <div data-testid={testid} className="space-y-1.5">
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted/20">
        <div className="bg-emerald-500" style={{ width: pct(y) }} />
        <div className="bg-red-500" style={{ width: pct(n) }} />
        <div className="bg-muted-foreground/40" style={{ width: pct(a) }} />
      </div>
      <div className="flex items-center justify-between text-[10px] mono">
        <span className="text-emerald-400">Yes {pct(y)}</span>
        <span className="text-red-400">No {pct(n)}</span>
        <span className="text-muted-foreground">Abstain {pct(a)}</span>
      </div>
    </div>
  );
};

// Inline label/value for compact stat rows inside cards.
export const GovKV = ({ label, children, testid }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mono">{label}</span>
    <span data-testid={testid} className="mono text-[13px] tabnum text-right break-all">{children}</span>
  </div>
);
